import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { Box, FormControlLabel, Paper } from "@mui/material";
import { Text } from "@semcore/typography";
import { Flex } from "@semcore/flex-box";
import axios from "axios";
import Switch from "react-switch";
import { Link } from "react-router-dom";

import {
  POST_WHITELABEL,
  POST_WHITELABEL_IMG,
  PUT_WHITELABEL,
  PUT_WHITELABEL_IMG,
  RESET_WHITELABEL,
} from "../../../services/constants";

import AddProjectStore from "../../../store/AddProjectStore";
import UploadImage from "../../share/components/white-label/UploadImage";
import FooterLinks from "../../share/components/white-label/FooterLinks";
import HeaderComponent from "../../share/components/white-label/Header.component";
import { useMemo } from "react";
import { fetchAndApplyWhiteLabel } from "../../share/custom-hook/whiteLabelUtils";
import { ZustandStore } from "../../../store/zustand-store";

import Alert from "@mui/material/Alert";
import FallbackLoader from "../../share/components/FallbackLoader";
import { handleInvitedUserAccess } from "../../../helperJs/helper";
import { useBuyAddonHandler } from "../../share/custom-hook/useBuyAddonHandler";

const WhiteLabel = () => {
  const email = localStorage.getItem("admin_mail");
  const { customer_plan, plan_loaded, userAccessData } = ZustandStore(
    (state) => state
  );
  const { white_lable, appliedWhiteLable, showNotification } = AddProjectStore(
    (state) => state
  );
  const { handleAddonPlan } = useBuyAddonHandler();

  const page_data = userAccessData?.[0]?.access;
  const isPageAccess = handleInvitedUserAccess("whiteLabelPlatform", page_data);

  const isWhiteLabel = useMemo(() => {
    const planName = customer_plan?.plan_name?.toLowerCase();
    if (!isPageAccess) {
      return {
        disabled: true,
        plan_type: "block_by_organisation",
      };
    }
    if (customer_plan.whitelabel_platform) {
      return {
        disabled: false,
        plan_type: null,
      };
    }

    if (planName === "rankspro lifetime plan") {
      return {
        disabled: true,
        plan_type: "plan addon",
      };
    }

    return {
      disabled: true,
      plan_type: "plan upgrade",
    };
  }, [customer_plan, isPageAccess]);

  function handleClick(event) {
    event.preventDefault();
    console.info("You clicked a breadcrumb.");
  }

  const handleToggleWhiteLabelSetting = (key) => {
    AddProjectStore.setState((state) => ({
      white_lable: {
        ...state.white_lable,
        isUpdate: true,
        [key]: !state.white_lable?.[key],
      },
    }));
  };

  const resetFaviconLogo = () => {
    AddProjectStore.setState({
      white_lable: {
        ...white_lable,
        favicon: null,
      },
    });
  };

  const handleFileUploadForFavIconLogo = (file) => {
    if (!file) return;
    const allowedTypes = ["image/x-icon", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      console.error("Invalid file type. Must be ICO, PNG, or GIF.");
      showNotification({
        message: "Invalid file type. Must be ICO, PNG, or GIF.",
        severity: "error",
      });
      resetFaviconLogo();
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onloadend = () => {
      img.onload = () => {
        const { width, height } = img;

        if (width < 16 || height < 16 || width > 48 || height > 48) {
          console.error("Image must be between 16x16 and 48x48 pixels.");
          showNotification({
            message: "Image must be between 16x16 and 48x48 pixels.",
            severity: "error",
          });
          resetFaviconLogo();
          return;
        }

        // Rename file here 🛠️
        const extension = file.name.split(".").pop();
        const renamedFile = new File([file], `${email}_favicon.${extension}`, {
          type: file.type,
        });
        AddProjectStore.setState({
          white_lable: {
            ...white_lable,
            isUpdate: true,
            favicon: {
              src: reader.result,
              type: renamedFile,
            },
          },
        });
      };

      img.src = reader.result;
    };

    reader.readAsDataURL(file);
  };

  const handlePdfLogoSave = async () => {
    const hasExistingLogos =
      appliedWhiteLable.header_logo || appliedWhiteLable.favicon;

    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("header", white_lable.header_logo?.type || "");
      formData.append("fav_icon", white_lable.favicon?.type || "");

      if (hasExistingLogos) {
        formData.append(
          "header_check",
          white_lable.header_logo?.src ? appliedWhiteLable.header_logo : ""
        );
        formData.append(
          "fav_icon_check",
          white_lable.favicon?.src ? appliedWhiteLable.favicon : ""
        );
      }

      const config = {
        method: hasExistingLogos ? "put" : "post",
        maxBodyLength: Infinity,
        url: hasExistingLogos ? PUT_WHITELABEL_IMG() : POST_WHITELABEL_IMG(),
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      };

      await axios.request(config);

      showNotification({
        message: "Logos saved successfully 🎉",
        severity: "success",
      });
    } catch (error) {
      console.error("Logo upload failed:", error);

      showNotification({
        message: error?.response?.data?.message || "Failed to save logos 🚫",
        severity: "error",
      });
    }
  };

  const handleResetWhiteLabel = async () => {
    try {
      const resp = await axios.delete(RESET_WHITELABEL(email));
      fetchAndApplyWhiteLabel(email);
      showNotification({
        message: "White Label Reset successfully 🎉",
        severity: "success",
      });
    } catch (error) {
      showNotification({
        message: "Unexpected error occurred 🤷‍♂️",
        severity: "error",
      });
      console.log(error);
    }
  };

  const handleApplyChangesToWhiteLabel = async () => {
    if (
      white_lable.show_footer_custom_links &&
      white_lable.custom_links.length === 0
    ) {
      showNotification({
        message: "Please add at least one Footer custom link to apply changes",
        severity: "error",
      });
      return;
    }
    if (
      white_lable.show_header_custom_links &&
      white_lable.header_custom_links.length === 0
    ) {
      showNotification({
        message: "Please add at least one Header custom link to apply changes",
        severity: "error",
      });
      return;
    }

    handlePdfLogoSave();
    try {
      const url = appliedWhiteLable?.id
        ? PUT_WHITELABEL(appliedWhiteLable.id)
        : POST_WHITELABEL();

      const response = await axios({
        method: appliedWhiteLable?.id ? "put" : "post",
        url,
        data: {
          email,
          data: [white_lable],
        },
      });

      showNotification({
        message: "White Label updated successfully",
        severity: "success",
      });

      await fetchAndApplyWhiteLabel(email); // Make sure this is awaited if it's async
    } catch (error) {
      console.error("WhiteLabel save error:", error);

      showNotification({
        message:
          error?.response?.data?.message || "Unexpected error occurred 🤷‍♂️",
        severity: "error",
      });
    }
  };

  const isReset = useMemo(() => {
    const {
      header_logo,
      favicon,
      custom_links,
      theme_color,
      header_custom_links,
      show_translation,
      show_upgrade,
      show_affiliate,
      show_header_links,
      show_footer_links,
    } = appliedWhiteLable;
    return Boolean(
      header_logo ||
        favicon ||
        custom_links?.length > 0 ||
        theme_color ||
        header_custom_links?.length > 0 ||
        !show_upgrade ||
        !show_translation ||
        !show_affiliate ||
        !show_header_links ||
        !show_footer_links
    );
  }, [appliedWhiteLable]);

  const addonLink = `https://buy.stripe.com/00w3cv4wi8Ik1DvgA90Fi1a?prefilled_email=${email}`;
  return (
    <>
      {plan_loaded ? (
        <>
          <div role="presentation" onClick={handleClick} className="my-3">
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                style={{ color: "#00000099" }}
                to="/account"
                className="anchor-hover"
              >
                Account
              </Link>
              <Link
                className="anchor-hover"
                style={{ color: "#00000099" }}
                to="/account/whitelabel"
              >
                White Label
              </Link>
              <Typography sx={{ color: "text.primary" }}>
                Branding Settings
              </Typography>
            </Breadcrumbs>
          </div>
          <Box>
            {isWhiteLabel.disabled && (
              <Alert severity="warning" className="mb-3">
                {isWhiteLabel.plan_type === "plan upgrade" ? (
                  <>
                    <Link
                      style={{ color: "#6a3be4" }}
                      to="/upgrade"
                      className="anchor-hover"
                    >
                      Upgrade
                    </Link>{" "}
                    your plan(Ultimate Plan) to unlock full branding control, or{" "}
                    <span
                      style={{ color: "#6a3be4" }}
                      className="anchor-hover"
                      onClick={() => handleAddonPlan(addonLink)}
                    >
                      Buy Addon.
                    </span>{" "}
                  </>
                ) : isWhiteLabel.plan_type === "block_by_organisation" ? (
                  <>
                    You cannot access white label platform, please contact the
                    account owner.
                  </>
                ) : (
                  <>
                    <span
                      style={{ color: "#6a3be4" }}
                      className="anchor-hover"
                      onClick={() => handleAddonPlan(addonLink)}
                    >
                      Buy Addon
                    </span>{" "}
                    to unlock full branding control.
                  </>
                )}
              </Alert>
            )}
            <Typography variant="h5">Branding Settings</Typography>
            <Text tag="label" size={300} mb={4} wMax={700}>
              To access the platform with no mention of Rankspro in the URL, use
              your own domain name.{" "}
              <Link to={"/account/whitelabel/domain"} className="anchor-hover">
                Custom domain names
              </Link>{" "}
            </Text>
            <HeaderComponent />
            <FooterLinks />
            <Paper
              elevation={3}
              className="p-3 mt-3"
              sx={{ boxShadow: "none", border: "2px solid #C4C7CF" }}
            >
              <Typography variant="subtitle1" gutterBottom>
                3. Sidebar
              </Typography>
              <Flex direction="column" gap={1}>
                <Text tag="label" size={200}>
                  Show Affiliate:
                </Text>
                <FormControlLabel
                  value="end"
                  control={
                    <Switch
                      className="react-switch"
                      onColor="#6a3be4"
                      onChange={() =>
                        handleToggleWhiteLabelSetting("show_affiliate")
                      }
                      checked={white_lable.show_affiliate}
                      aria-labelledby="neat-label"
                      uncheckedIcon={false}
                      checkedIcon={false}
                      width={40}
                      height={20}
                    />
                  }
                  label={white_lable.show_affiliate ? "On" : "Off"}
                  labelPlacement="end"
                  sx={{
                    margin: 0,
                    "& .MuiTypography-root": {
                      marginLeft: "10px",
                    },
                  }}
                />
              </Flex>
            </Paper>

            <Paper
              elevation={3}
              className="p-3 mt-3"
              sx={{ boxShadow: "none", border: "2px solid #C4C7CF" }}
            >
              <Typography variant="subtitle1" gutterBottom>
                4. Favicon
              </Typography>
              <Flex direction="column" gap={1} w={350}>
                <UploadImage
                  key={"footer"}
                  type="footer"
                  onChange={handleFileUploadForFavIconLogo}
                  resetLogo={resetFaviconLogo}
                  Logo={white_lable.favicon}
                  note={"Must be ICO, PNG, or GIF.16x16 px to 48x48 px"}
                />
              </Flex>
            </Paper>
            <Box className="mt-3 mb-5">
              <Flex gap={2}>
                <button
                  className="white-lable-btn"
                  disabled={isWhiteLabel.disabled || !isReset}
                  onClick={handleResetWhiteLabel}
                  style={{
                    backgroundColor: "transparent",
                    color: "#6a3be4",
                    border: "1px solid #6a3be4",
                  }}
                >
                  Reset Changes
                </button>
                <button
                  className="white-lable-btn"
                  disabled={isWhiteLabel.disabled || !white_lable.isUpdate}
                  onClick={handleApplyChangesToWhiteLabel}
                >
                  Save Changes
                </button>
              </Flex>
            </Box>
          </Box>
        </>
      ) : (
        <FallbackLoader />
      )}
    </>
  );
};

export default WhiteLabel;
